import { useCallback } from 'react';

import { useBatchComponentUpdateContext } from '../shared/batch-component-update.context';
import {
  SettingsGroup,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import { ColourTokenRadio } from 'approot/shared/colour/colour-token';
import Switch from 'approot/shared/switch/switch';
import { Dropdown, DropdownIndicator } from 'approot/shared/dropdown/dropdown';
import ActivityBuilderInlineSvg from 'approot/shared/inline-svg/activity-builder/activity-builder-inline-svg';
import { SETTINGS_MENU_ID } from 'approot/activity-builder/settings/settings-component-menu';
import {
  COMPONENT_COLOURS,
  ComponentSettingsBordersProps,
} from './component-settings-borders.constants';
import {
  ComponentSettings,
  ComponentDropdownButton,
  ComponentDropdownSwitch,
  ComponentSettingsGroup,
} from './component-settings-borders.style';
import type { ComponentOuterStyleFragment } from '../../lesson-builder.graphql.generated';

const ComponentSettingsBorders = ({
  componentId,
  sectionId,
  style,
  type,
}: ComponentSettingsBordersProps) => {
  const { updateComponentProperties } = useBatchComponentUpdateContext();

  const defaultStyle: ComponentOuterStyleFragment = {
    __typename: 'ComponentStyle',
    outerBorder: null,
    outerBaseColour: null,
    outerBackgroundColour: null,
  };

  const currentStyle = style || defaultStyle;

  const handleUpdate = useCallback(
    (newStyle: ComponentOuterStyleFragment) => {
      const styleData = {
        ...currentStyle,
        outerBorder: newStyle.outerBorder,
        outerBaseColour: newStyle.outerBaseColour,
        outerBackgroundColour: newStyle.outerBackgroundColour,
      };

      updateComponentProperties({
        isSaveImmediate: true,
        input: {
          sectionId,
          componentId,
          componentData: {
            properties: {
              style: {
                ...styleData,
              },
            },
          },
        },
        typename: type,
      });
    },
    [componentId, sectionId, type, updateComponentProperties, currentStyle]
  );

  const hasBorders = !!currentStyle.outerBorder;
  const hasBackground = !!currentStyle.outerBackgroundColour;

  return (
    <ComponentSettings>
      <Dropdown
        isSmall
        menuWidth={226}
        menuContainerElement={SETTINGS_MENU_ID}
        strategy="fixed"
        closeMenuOnSelect={false}
        dropdownButtonContents={
          <ComponentDropdownButton data-selector="component-borders">
            <ActivityBuilderInlineSvg
              id="icon_paint_pallet"
              width={20}
              height={20}
            />
            <DropdownIndicator />
          </ComponentDropdownButton>
        }
        dropdownContents={
          <>
            <SettingsGroup as="fieldset" marginBottom={0}>
              <ComponentDropdownSwitch>
                <span>Borders</span>
                <Switch
                  data-dropdown-item="true"
                  ariaLabel={`Click to ${hasBorders ? 'hide' : 'show'} border`}
                  state={hasBorders}
                  onClick={() => {
                    handleUpdate({
                      ...currentStyle,
                      outerBorder: !hasBorders,
                      outerBaseColour:
                        currentStyle.outerBaseColour ||
                        COMPONENT_COLOURS[0].base,
                    });
                  }}
                />
              </ComponentDropdownSwitch>
              <ComponentDropdownSwitch>
                <span>Background</span>
                <Switch
                  data-dropdown-item="true"
                  ariaLabel={`Click to ${
                    hasBackground ? 'hide' : 'show'
                  } background`}
                  state={hasBackground}
                  onClick={() => {
                    handleUpdate({
                      ...currentStyle,
                      outerBackgroundColour: !hasBackground,
                      outerBaseColour:
                        currentStyle.outerBaseColour ||
                        COMPONENT_COLOURS[0].base,
                    });
                  }}
                />
              </ComponentDropdownSwitch>
            </SettingsGroup>

            {(hasBorders || hasBackground) && (
              <ComponentSettingsGroup>
                <SettingsLabel aria-label="Choose a colour">
                  Colour
                </SettingsLabel>
                <div role="radiogroup">
                  {COMPONENT_COLOURS.map(item => (
                    <ColourTokenRadio
                      key={item.colour1}
                      name="borderColour"
                      data-dropdown-item="true"
                      colours={item}
                      isActive={item.base === currentStyle.outerBaseColour}
                      onChange={() => {
                        handleUpdate({
                          ...currentStyle,
                          outerBaseColour: item.base,
                        });
                      }}
                    />
                  ))}
                </div>
              </ComponentSettingsGroup>
            )}
          </>
        }
      />
    </ComponentSettings>
  );
};

export default ComponentSettingsBorders;
